import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription, interval } from 'rxjs';
import {
  CrawlService,
  CrawlStatus,
  CrawlEstimate,
  CompetitorReadiness,
  CompetitorStatus,
  DomainProcessingStatus,
  ProcessingJobType,
  SitemapTreeNode,
  LocaleGroup
} from '../../services/crawl';
import { DomainContextService } from '../../services/domain-context';
import { CompareContextService } from '../../services/compare-context';
import { NotificationService } from '../../services/notification.service';
import { FeatureFlagService } from '../../services/feature-flag';

type SetupStepState =
  | 'no_data'
  | 'not_started'
  | 'waiting'
  | 'queued'
  | 'running'
  | 'partial'
  | 'complete'
  | 'blocked'
  | 'failed';

interface SetupStep {
  key: string;
  title: string;
  description: string;
  /** `process/<endpoint>` suffix; absent for the crawl step. */
  endpoint?: string;
  /** Queue job type used to detect queued/running state. */
  jobType?: ProcessingJobType;
  state: SetupStepState;
  detail: string;
  /** Optional explanatory hint shown under the detail line (not an error). */
  note?: string;
  blockedReason?: string | null;
  /** Opt-in steps are excluded from the default Run all + progress. */
  optional?: boolean;
  /** Whether this step exposes a primary Run/Refresh action. */
  actionable: boolean;
  actionLabel: string;
}

@Component({
  selector: 'app-setup',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './setup.html',
  styleUrl: './setup.css'
})
export class SetupComponent implements OnInit, OnDestroy {
  domain = '';
  domains: string[] = [];
  loading = false;
  error: string | null = null;

  crawlStatus: CrawlStatus | null = null;
  status: DomainProcessingStatus | null = null;

  coreSteps: SetupStep[] = [];
  optionalSteps: SetupStep[] = [];

  runningAll = false;
  busyByKey = new Map<string, boolean>();

  /**
   * Upstream dependencies per step key. A step's Run action is disabled while any
   * of its (transitive) upstream dependencies is queued or running, so you can't
   * fire a step whose input is still being produced.
   */
  private readonly stepDeps: Record<string, string[]> = {
    'links-classify': ['links-backfill'],
    'topics-aggregate': ['topics-map'],
    'internal-links': ['links-classify'],
    'supporting-by-topic': ['topics-aggregate'],
    'supporting-opportunities': ['topics-aggregate']
  };

  // Opt-in toggles (cost/time-sensitive steps).
  includeKeywords = false;
  includeOpportunities = false;

  // Dev-only: scope the workspace-global topics-map step to a subset of domains
  // (focal + competitors) to avoid clustering every domain in the DB.
  topicsMapScope: 'all' | 'selected' = 'all';
  topicsMapDomains = new Set<string>();

  // Competitors (per focal domain).
  competitorStatus: CompetitorStatus | null = null;
  runningCompetitorPrepAll = false;
  competitorBusy = new Map<string, boolean>();
  markCompetitorInput = '';

  // Add-domain flow. `addMode` decides whether the crawled domain becomes the
  // new focal selection or is marked as a competitor of the current domain.
  showAddDomain = false;
  addMode: 'focal' | 'competitor' = 'focal';
  newDomainInput = '';
  estimate: CrawlEstimate | null = null;
  estimateLoading = false;
  estimateError: string | null = null;
  startingCrawl = false;
  /** True when remote sitemap discovery was blocked by bot protection. */
  botProtected = false;
  /** Uploaded .xml files used instead of remote sitemap fetch. */
  uploadedSitemapFiles: File[] = [];
  usingUploadedSitemaps = false;

  // Sitemap scoping (mirrors Crawl Management granularity).
  readonly defaultCrawlLimit = 750;
  private readonly devCrawlLimit = 10000;
  bypassCrawlCap = false;
  localePartitionedSitemaps = false;
  availableLeafSitemaps: Array<{ sitemapUrl: string; urlCount: number }> = [];
  selectedSitemaps = new Set<string>();
  availableLocaleGroups: LocaleGroup[] = [];
  selectedLocales = new Set<string>();
  minLastmodFrom = '';
  perSitemapLimits: Record<string, number> = {};
  perSitemapLimitMode: 'recent' | 'oldest' | 'balanced' = 'recent';
  private estimateDebounce: ReturnType<typeof setTimeout> | null = null;

  private readonly POLL_MS = 6000;
  private subs = new Subscription();
  private pollSub: Subscription | null = null;

  constructor(
    private crawl: CrawlService,
    private domainContext: DomainContextService,
    private compareContext: CompareContextService,
    private notifications: NotificationService,
    public featureFlags: FeatureFlagService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.domainContext.ensureInitialized();
    this.subs.add(this.domainContext.domains$.subscribe((d) => (this.domains = d)));
    this.subs.add(
      this.domainContext.selectedDomain$.subscribe((domain) => {
        this.domain = domain;
        if (domain) {
          this.loadAll();
        } else {
          this.crawlStatus = null;
          this.status = null;
          this.coreSteps = [];
          this.optionalSteps = [];
          this.competitorStatus = null;
        }
      })
    );
    this.startPolling();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.stopPolling();
    if (this.estimateDebounce) clearTimeout(this.estimateDebounce);
  }

  onDomainChange(domain: string): void {
    this.domainContext.setSelectedDomain(domain);
  }

  // --- Data loading -------------------------------------------------------

  loadAll(showSpinner = true): void {
    if (!this.domain) return;
    if (showSpinner) this.loading = true;
    this.error = null;

    this.crawl.getCrawlStatus(this.domain).subscribe({
      next: (cs) => (this.crawlStatus = cs),
      error: () => (this.crawlStatus = null)
    });

    this.crawl.getProcessingStatus(this.domain).subscribe({
      next: (st) => {
        this.status = st;
        this.rebuildSteps();
        this.loading = false;
      },
      error: (err) => {
        this.error = err?.error?.message || err?.message || 'Failed to load processing status.';
        this.loading = false;
      }
    });

    this.crawl.getCompetitorStatus(this.domain).subscribe({
      next: (res) => (this.competitorStatus = res.data),
      error: () => (this.competitorStatus = null)
    });
  }

  private startPolling(): void {
    if (this.pollSub) return;
    this.pollSub = interval(this.POLL_MS).subscribe(() => {
      // Pause polling while the Add-domain panel is open — re-rendering the
      // sitemap scoping UI mid-interaction would steal clicks/focus and reset
      // the live estimate.
      if (this.domain && !this.showAddDomain) this.loadAll(false);
    });
  }

  private stopPolling(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = null;
  }

  // --- Step model ---------------------------------------------------------

  private jobStateFor(type: ProcessingJobType): 'queued' | 'running' | null {
    const jobs = this.status?.activeJobs || [];
    const running = jobs.find((j) => j.type === type && j.status === 'running');
    if (running) return 'running';
    const queued = jobs.find((j) => j.type === type && j.status === 'queued');
    if (queued) return 'queued';
    return null;
  }

  /** Map a data-presence status + queue state into a unified step state. */
  private resolveState(
    dataStatus: string | undefined,
    jobType?: ProcessingJobType
  ): SetupStepState {
    if (jobType) {
      const queueState = this.jobStateFor(jobType);
      if (queueState) return queueState;
    }
    switch (dataStatus) {
      case 'complete':
        return 'complete';
      case 'partial':
        return 'partial';
      case 'blocked':
        return 'blocked';
      // `no_data` (zero analyzed pages) reads as "Not started" to users, so we
      // collapse the two — the only honest distinction (waiting on the crawl) is
      // handled by the crawl-completion gate in rebuildSteps().
      case 'no_data':
        return 'not_started';
      default:
        return 'not_started';
    }
  }

  /** True once the crawl has finished crawling AND analyzing all pages. */
  get analysisComplete(): boolean {
    const cs = this.crawlStatus;
    return Boolean(cs && (cs.isComplete || cs.phase === 'complete'));
  }

  private rebuildSteps(): void {
    const st = this.status;
    if (!st) {
      this.coreSteps = [];
      this.optionalSteps = [];
      return;
    }

    const totalPages = st.linksBackfill?.totalPages ?? 0;
    const cs = this.crawlStatus;
    const phase = cs?.phase;
    const pagesCrawled = cs?.pagesCrawled ?? 0;
    const pagesAnalyzed = cs?.pagesAnalyzed ?? 0;
    // During the crawl phase totalUrls is the target (maxPages); during/after
    // analysis the backend reports totalUrls == pagesFound (the real crawled set).
    const crawlTotal = cs?.totalUrls ?? 0;
    const analysisComplete = this.analysisComplete;

    // Step 1 — Crawl: just the fetch. Considered done the moment we move on to
    // analysis, so the number never jumps backward (e.g. 715/715 -> 15/715).
    let crawlState: SetupStepState;
    if (!cs) crawlState = 'not_started';
    else if (cs.isComplete || phase === 'complete' || phase === 'analyzing') crawlState = 'complete';
    else if (phase === 'crawling') crawlState = 'running';
    else if (pagesCrawled > 0) crawlState = 'partial';
    else crawlState = 'not_started';

    // Step 2 — Analyze: our processing of crawled pages into PageAnalysis. This
    // is where the per-page work happens, so it owns the "X / Y pages" climb.
    const analyzeDenom = pagesCrawled || crawlTotal;
    let analyzeState: SetupStepState;
    if (!cs) analyzeState = 'not_started';
    else if (cs.isComplete || phase === 'complete') analyzeState = 'complete';
    else if (phase === 'analyzing') analyzeState = 'running';
    else analyzeState = 'not_started'; // still crawling — analysis hasn't begun

    // Everything downstream of analysis is gated until the crawl AND analysis are
    // done. While we wait we render "Waiting for crawl" instead of evaluating
    // partial/complete against an incomplete, growing page set (which caused the
    // confusing flicker and premature "Complete" badges). A job already in the
    // queue still shows its real running/queued state.
    const gate = (state: SetupStepState): SetupStepState => {
      if (analysisComplete) return state;
      if (state === 'queued' || state === 'running') return state;
      return 'waiting';
    };

    // Topics map is workspace-global, so existing mappings from other domains make
    // it look "Complete" the instant a new domain is crawled. staleForDomain (set
    // by the backend when the last map predates this domain's analysis) flags that
    // it must be re-run to incorporate this domain.
    let topicsMapState = gate(this.resolveState(st.topicsMap?.status, 'topics-map'));
    const topicsMapStale = Boolean(st.topicsMap?.staleForDomain);
    if (analysisComplete && topicsMapStale && topicsMapState !== 'queued' && topicsMapState !== 'running') {
      topicsMapState = 'not_started';
    }
    const topicsMapDetail = topicsMapStale
      ? `${st.topicsMap?.mappingCount ?? 0} mappings · re-run to include this domain`
      : `${st.topicsMap?.mappingCount ?? 0} mappings`;

    // Est. traffic: DataForSEO Labs only reports organic ETV for pages that
    // actually rank, so PARTIAL is expected (not a failure). Spell out the gap.
    const trafficTotal = st.estTraffic?.totalPages ?? totalPages;
    const trafficWith = st.estTraffic?.pagesWithTraffic ?? 0;
    const trafficState = gate(this.resolveState(st.estTraffic?.status));
    const trafficNote =
      analysisComplete && trafficState !== 'waiting' && trafficWith < trafficTotal
        ? `${trafficTotal - trafficWith} pages have no DataForSEO organic-traffic data — they don't rank organically, so they stay blank. Partial is expected here, not a failure.`
        : undefined;

    // Internal-link suggestions don't need topics-map/aggregate, but they DO use
    // classified link types + per-page topic signals + est. traffic for scoring.
    // If a build exists but classification isn't finished, the suggestions are
    // built against partial data — flag them as stale so the operator re-runs.
    const internalLinksState = gate(this.resolveState(st.internalLinkSuggestions?.status, 'action-plan-internal-links'));
    const classifyComplete = st.linksClassify?.status === 'complete';
    const internalLinksStale = internalLinksState === 'complete' && !classifyComplete;
    const internalLinksNote = internalLinksStale
      ? 'Built before link classification finished — re-run (via Finish setup) once Links classify completes for accurate scoring.'
      : undefined;

    // Supporting-pages builds cache the competitor set they were built against.
    // When the marked-competitor set changes, the backend reads return null and
    // the snapshot is stale — flag it as needs-run so the operator rebuilds it.
    const competitorStaleNote = 'Competitor set changed — re-run to include the current competitors.';
    const byTopicStale = analysisComplete && Boolean(st.supportingPagesByTopic?.staleForCompetitorSet);
    let supportingByTopicState = gate(this.resolveState(st.supportingPagesByTopic?.status, 'supporting-pages-by-topic'));
    if (byTopicStale && supportingByTopicState === 'complete') supportingByTopicState = 'not_started';
    const oppStale = analysisComplete && Boolean(st.supportingPagesOpportunities?.staleForCompetitorSet);
    let supportingOppState = gate(this.resolveState(st.supportingPagesOpportunities?.status, 'supporting-pages-opportunities'));
    if (oppStale && supportingOppState === 'complete') supportingOppState = 'not_started';

    this.coreSteps = [
      {
        key: 'crawl',
        title: 'Crawl',
        description: 'Fetch pages from the sitemap.',
        state: crawlState,
        detail: crawlTotal ? `${pagesCrawled} / ${crawlTotal} pages crawled` : `${pagesCrawled} pages crawled`,
        actionable: true,
        actionLabel: 'Open Crawl Management'
      },
      {
        key: 'analyze',
        title: 'Analyze',
        description: 'Process crawled pages into page analyses.',
        state: analyzeState,
        detail: analyzeDenom ? `${pagesAnalyzed} / ${analyzeDenom} pages analyzed` : `${pagesAnalyzed} pages analyzed`,
        actionable: false,
        actionLabel: ''
      },
      {
        key: 'est-traffic',
        title: 'Estimated traffic',
        description: 'Organic traffic estimates (DataForSEO Labs).',
        endpoint: 'traffic-enrichment',
        state: trafficState,
        detail: `${trafficWith} / ${trafficTotal} pages with traffic data`,
        note: trafficNote,
        actionable: true,
        actionLabel: 'Refresh'
      },
      {
        key: 'links-backfill',
        title: 'Links backfill',
        description: 'Fetch internal links from the crawl.',
        endpoint: 'links-backfill',
        jobType: 'links-backfill',
        state: gate(this.resolveState(st.linksBackfill?.status, 'links-backfill')),
        detail: `${st.linksBackfill?.pagesWithLinks ?? 0} / ${st.linksBackfill?.totalPages ?? 0} pages`,
        actionable: true,
        actionLabel: 'Run'
      },
      {
        key: 'links-classify',
        title: 'Links classify',
        description: 'Classify internal links (structural vs contextual).',
        endpoint: 'links-classify',
        jobType: 'links-classify',
        state: gate(this.resolveState(st.linksClassify?.status, 'links-classify')),
        detail: `${st.linksClassify?.pagesWithClassifiedLinks ?? 0} / ${st.linksClassify?.pagesWithLinks ?? 0} pages`,
        actionable: true,
        actionLabel: 'Run'
      },
      {
        key: 'topics-map',
        title: 'Topics map',
        description: 'Cluster topic signals into parent topics (workspace-global).',
        endpoint: 'topics-map',
        jobType: 'topics-map',
        state: topicsMapState,
        detail: topicsMapDetail,
        actionable: true,
        actionLabel: 'Run'
      },
      {
        key: 'topics-aggregate',
        title: 'Topics aggregate',
        description: 'Aggregate page topics into domain authority.',
        endpoint: 'topics-aggregate',
        jobType: 'topics-aggregate',
        state: gate(this.resolveState(st.topicsAggregate?.status, 'topics-aggregate')),
        detail: `${st.topicsAggregate?.aggregateCount ?? 0} topics`,
        actionable: true,
        actionLabel: 'Run'
      },
      {
        key: 'internal-links',
        title: 'Internal link suggestions',
        description: 'Build the internal-linking action plan.',
        endpoint: 'internal-link-suggestions',
        jobType: 'action-plan-internal-links',
        state: internalLinksState,
        detail: `${st.internalLinkSuggestions?.suggestionCount ?? 0} suggestions`,
        note: internalLinksNote,
        blockedReason: st.internalLinkSuggestions?.blockedReason,
        actionable: true,
        actionLabel: 'Run'
      },
      {
        key: 'supporting-by-topic',
        title: 'Supporting pages by topic',
        description: 'Topic-diagnostic supporting page snapshots.',
        endpoint: 'supporting-pages-by-topic',
        jobType: 'supporting-pages-by-topic',
        state: supportingByTopicState,
        detail: this.supportingDetail(st.supportingPagesByTopic),
        note: byTopicStale ? competitorStaleNote : undefined,
        blockedReason: st.supportingPagesByTopic?.blockedReason,
        actionable: true,
        actionLabel: 'Run'
      }
    ];

    this.optionalSteps = [
      {
        key: 'est-keywords',
        title: 'Estimated keywords',
        description: 'Ranked keywords domain dump (DataForSEO Labs — incurs cost).',
        endpoint: 'ranked-keywords-enrichment',
        state: gate(this.resolveState(st.estKeywords?.status)),
        detail: `${st.estKeywords?.pagesWithKeywords ?? 0} / ${st.estKeywords?.totalPages ?? totalPages} pages`,
        optional: true,
        actionable: true,
        actionLabel: 'Run (Labs cost)'
      },
      {
        key: 'supporting-opportunities',
        title: 'Supporting page opportunities',
        description: 'Content-gap opportunities (requires the content-gap sidecar; slow).',
        endpoint: 'supporting-pages-opportunities',
        jobType: 'supporting-pages-opportunities',
        state: supportingOppState,
        detail: this.supportingDetail(st.supportingPagesOpportunities),
        note: oppStale ? competitorStaleNote : undefined,
        blockedReason: st.supportingPagesOpportunities?.blockedReason,
        optional: true,
        actionable: true,
        actionLabel: 'Run'
      }
    ];
  }

  private supportingDetail(s?: { snapshotCount: number; processedUrls: number; totalUrls: number; progressPercent: number }): string {
    if (!s) return '';
    if (s.totalUrls > 0 && s.processedUrls < s.totalUrls) {
      return `${s.processedUrls} / ${s.totalUrls} URLs (${s.progressPercent}%)`;
    }
    return `${s.snapshotCount} snapshots`;
  }

  // --- Progress -----------------------------------------------------------

  get completeCount(): number {
    return this.coreSteps.filter((s) => s.state === 'complete').length;
  }

  get totalCount(): number {
    return this.coreSteps.length;
  }

  get progressPercent(): number {
    if (this.totalCount === 0) return 0;
    return Math.round((this.completeCount / this.totalCount) * 100);
  }

  // --- Finish setup / Run all --------------------------------------------

  get crawlReady(): boolean {
    return Boolean(this.crawlStatus?.isComplete) && (this.crawlStatus?.pagesAnalyzed ?? this.status?.linksBackfill?.totalPages ?? 0) > 0;
  }

  /** True while any pipeline step is queued/running or a request is in flight. */
  get pipelineBusy(): boolean {
    if (this.runningAll) return true;
    for (const v of this.busyByKey.values()) if (v) return true;
    return [...this.coreSteps, ...this.optionalSteps].some(
      (s) => s.state === 'queued' || s.state === 'running'
    );
  }

  get finishSetupBlockedReason(): string | null {
    if (!this.crawlStatus) return 'No crawl yet — start a crawl first.';
    if (!this.crawlStatus.isComplete) return 'Crawl still in progress — wait for it to finish.';
    const pages = this.crawlStatus.pagesAnalyzed ?? this.status?.linksBackfill?.totalPages ?? 0;
    if (pages <= 0) return 'Crawl produced 0 analyzed pages — nothing to process.';
    if (this.pipelineBusy) return 'Processing is already running — wait for the current steps to finish.';
    return null;
  }

  finishSetup(): void {
    if (!this.domain || this.runningAll) return;
    if (this.finishSetupBlockedReason) {
      this.notifications.error(this.finishSetupBlockedReason);
      return;
    }
    this.runningAll = true;
    const scopedDomains =
      this.isDevView() && this.topicsMapScope === 'selected'
        ? Array.from(this.topicsMapDomains)
        : undefined;
    this.crawl.runAllProcessing(this.domain, scopedDomains).subscribe({
      next: (res) => {
        this.notifications.success(res?.message || 'All processing steps queued.');
        this.runningAll = false;
        // Opt-in steps are not part of run-all; trigger them if toggled.
        if (this.includeKeywords) this.runStepByKey('est-keywords', true);
        if (this.includeOpportunities) this.runStepByKey('supporting-opportunities', true);
        this.loadAll(false);
      },
      error: (err) => {
        this.notifications.error(err?.error?.message || err?.message || 'Failed to queue processing.');
        this.runningAll = false;
      }
    });
  }

  // --- Per-step run -------------------------------------------------------

  isBusy(key: string): boolean {
    return this.busyByKey.get(key) === true;
  }

  private stepByKey(key: string): SetupStep | undefined {
    return [...this.coreSteps, ...this.optionalSteps].find((s) => s.key === key);
  }

  /** True if any (transitive) upstream dependency is queued/running/busy. */
  private hasUpstreamInFlight(key: string, seen = new Set<string>()): boolean {
    for (const dep of this.stepDeps[key] || []) {
      if (seen.has(dep)) continue;
      seen.add(dep);
      if (this.isBusy(dep)) return true;
      const s = this.stepByKey(dep);
      if (s && (s.state === 'queued' || s.state === 'running')) return true;
      if (this.hasUpstreamInFlight(dep, seen)) return true;
    }
    return false;
  }

  /** Whether a step's Run/Refresh action should be disabled. */
  isStepActionDisabled(step: SetupStep): boolean {
    return this.stepActionDisabledReason(step) !== null;
  }

  /** Human-readable reason a step's action is disabled (also used as tooltip). */
  stepActionDisabledReason(step: SetupStep): string | null {
    // Crawl's action is pure navigation — always available.
    if (step.key === 'crawl') return null;
    if (this.isBusy(step.key)) return 'Starting…';
    if (step.state === 'queued') return 'Already queued.';
    if (step.state === 'running') return 'Already running.';
    if (step.state === 'waiting') return 'Waiting for the crawl to finish.';
    // Blocked steps can't run until the prerequisite is fixed elsewhere (e.g.
    // mark a competitor). The sidecar-down case is the exception: the fix is to
    // start the sidecar and retry, so keep that one clickable.
    if (step.state === 'blocked' && !this.isSidecarBlocked(step)) {
      return step.blockedReason || 'Blocked — resolve the prerequisite first.';
    }
    if (this.hasUpstreamInFlight(step.key)) {
      return 'A prerequisite step is still queued/running — wait for it to finish.';
    }
    return null;
  }

  runStep(step: SetupStep): void {
    if (step.key === 'crawl') {
      this.router.navigate(['/crawl-management']);
      return;
    }
    if (this.isStepActionDisabled(step)) return;
    this.runStepByKey(step.key);
  }

  private runStepByKey(key: string, silent = false): void {
    const step = [...this.coreSteps, ...this.optionalSteps].find((s) => s.key === key);
    if (!step || !step.endpoint || !this.domain) return;
    if (this.isBusy(key)) return;
    this.busyByKey.set(key, true);
    this.crawl.runProcessEndpoint(this.domain, step.endpoint).subscribe({
      next: (res) => {
        if (!silent) this.notifications.success(res?.message || `${step.title} started.`);
        this.busyByKey.set(key, false);
        this.loadAll(false);
      },
      error: (err) => {
        if (!silent) this.notifications.error(err?.error?.message || err?.message || `Failed to start ${step.title}.`);
        this.busyByKey.set(key, false);
      }
    });
  }

  // --- Competitors --------------------------------------------------------

  get competitors(): CompetitorReadiness[] {
    return this.competitorStatus?.competitors || [];
  }

  /** Any competitor that is crawled + has topic signals but isn't ready/running. */
  get hasPreppableCompetitors(): boolean {
    return this.competitors.some((c) => this.canPrepCompetitor(c));
  }

  competitorChip(c: CompetitorReadiness): { label: string; cls: string } {
    if (c.activeJob === 'running') return { label: 'Prepping…', cls: 'state-running' };
    if (c.activeJob === 'queued') return { label: 'Queued', cls: 'state-queued' };
    if (c.ready) return { label: 'Ready', cls: 'state-complete' };
    if (!c.crawled) return { label: 'Not crawled', cls: 'state-not_started' };
    if (this.competitorContentBlocked(c)) return { label: 'No content captured', cls: 'state-blocked' };
    if (!c.hasTopicSignals) return { label: 'Analyzing', cls: 'state-waiting' };
    return { label: 'Needs topic prep', cls: 'state-not_started' };
  }

  /**
   * Pages were analyzed but none captured extractable text — the crawler got a
   * head-only/error shell (client-rendered or bot-blocked site), so no topic
   * signals can be extracted. Distinct from "still analyzing".
   */
  competitorContentBlocked(c: CompetitorReadiness): boolean {
    return c.crawled && !c.hasTopicSignals && c.pagesAnalyzed > 0 && (c.pagesWithContent ?? 0) === 0;
  }

  canPrepCompetitor(c: CompetitorReadiness): boolean {
    return c.crawled && c.hasTopicSignals && !c.ready && c.activeJob === null;
  }

  isCompetitorBusy(domain: string): boolean {
    return this.competitorBusy.get(domain) === true;
  }

  runCompetitorPrep(c: CompetitorReadiness): void {
    if (!this.canPrepCompetitor(c) || this.isCompetitorBusy(c.domain)) return;
    this.competitorBusy.set(c.domain, true);
    this.crawl.runCompetitorPrep(c.domain).subscribe({
      next: (res) => {
        this.notifications.success(res?.message || `Topic prep queued for ${c.domain}.`);
        this.competitorBusy.set(c.domain, false);
        this.loadAll(false);
      },
      error: (err) => {
        this.notifications.error(err?.error?.message || err?.message || 'Failed to queue topic prep.');
        this.competitorBusy.set(c.domain, false);
      }
    });
  }

  runCompetitorPrepAll(): void {
    if (!this.domain || this.runningCompetitorPrepAll) return;
    this.runningCompetitorPrepAll = true;
    this.crawl.runCompetitorPrepAll(this.domain).subscribe({
      next: (res) => {
        this.notifications.success(res?.message || 'Topic prep queued for all competitors.');
        this.runningCompetitorPrepAll = false;
        this.loadAll(false);
      },
      error: (err) => {
        this.notifications.error(err?.error?.message || err?.message || 'Failed to queue topic prep.');
        this.runningCompetitorPrepAll = false;
      }
    });
  }

  removeCompetitor(c: CompetitorReadiness): void {
    if (!this.domain || this.isCompetitorBusy(c.domain)) return;
    this.competitorBusy.set(c.domain, true);
    this.compareContext.removeMarkedCompetitor(c.domain, this.domain).subscribe({
      next: () => {
        this.notifications.success(`Removed ${c.domain} as a competitor.`);
        this.competitorBusy.set(c.domain, false);
        this.loadAll(false);
      },
      error: (err) => {
        this.notifications.error(err?.error?.message || err?.message || 'Failed to remove competitor.');
        this.competitorBusy.set(c.domain, false);
      }
    });
  }

  markExistingCompetitor(): void {
    const competitor = this.normalizeDomain(this.markCompetitorInput);
    if (!competitor || !this.domain) {
      this.notifications.error('Enter a domain to mark as a competitor.');
      return;
    }
    if (competitor === this.normalizeDomain(this.domain)) {
      this.notifications.error('A domain cannot be its own competitor.');
      return;
    }
    this.compareContext.addMarkedCompetitor(competitor, this.domain).subscribe({
      next: () => {
        this.notifications.success(`Marked ${competitor} as a competitor.`);
        this.markCompetitorInput = '';
        this.loadAll(false);
      },
      error: (err) => {
        this.notifications.error(err?.error?.message || err?.message || 'Failed to mark competitor.');
      }
    });
  }

  // --- Add domain ---------------------------------------------------------

  openAddDomain(mode: 'focal' | 'competitor'): void {
    this.addMode = mode;
    this.showAddDomain = true;
    this.resetAddDomainForm();
    // The Add panel renders at the top of the page, but this is triggered from
    // the Competitors card at the bottom — scroll it into view so it doesn't
    // look like nothing happened.
    setTimeout(() => {
      document.getElementById('setup-add-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  }

  toggleAddDomain(): void {
    this.showAddDomain = !this.showAddDomain;
    if (this.showAddDomain) this.addMode = 'focal';
    this.resetAddDomainForm();
  }

  private resetAddDomainForm(): void {
    this.estimate = null;
    this.estimateError = null;
    this.botProtected = false;
    this.uploadedSitemapFiles = [];
    this.usingUploadedSitemaps = false;
    this.bypassCrawlCap = false;
    this.localePartitionedSitemaps = false;
    this.availableLeafSitemaps = [];
    this.selectedSitemaps.clear();
    this.availableLocaleGroups = [];
    this.selectedLocales.clear();
    this.perSitemapLimits = {};
    this.minLastmodFrom = '';
  }

  private normalizeDomain(value: string): string {
    return value
      .toLowerCase()
      .trim()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .split('/')[0];
  }

  estimateNewDomain(preserveSelection = false): void {
    const domain = this.normalizeDomain(this.newDomainInput);
    if (!domain) {
      this.estimateError = 'Enter a domain (e.g. example.com).';
      return;
    }
    if (this.usingUploadedSitemaps && this.uploadedSitemapFiles.length > 0) {
      this.estimateFromUploadedSitemaps(preserveSelection);
      return;
    }
    this.estimateLoading = true;
    this.estimateError = null;
    this.botProtected = false;
    if (!preserveSelection) {
      this.perSitemapLimits = {};
    }
    this.crawl
      .estimateCrawl(domain, this.buildEstimateOptions(preserveSelection))
      .subscribe({
        next: (res) => this.applyEstimateResponse(res.data, preserveSelection),
        error: (err) => this.handleEstimateError(err)
      });
  }

  onSitemapFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []).filter((file) =>
      /\.(xml|gz)$/i.test(file.name)
    );
    input.value = '';
    if (files.length === 0) {
      this.notifications.error('Choose one or more .xml sitemap files.');
      return;
    }
    const domain = this.normalizeDomain(this.newDomainInput);
    if (!domain) {
      this.estimateError = 'Enter a domain first, then upload sitemap files.';
      return;
    }
    this.uploadedSitemapFiles = files;
    this.usingUploadedSitemaps = true;
    this.botProtected = false;
    this.estimateError = null;
    this.estimateFromUploadedSitemaps(false);
  }

  clearUploadedSitemaps(): void {
    this.uploadedSitemapFiles = [];
    this.usingUploadedSitemaps = false;
    this.estimate = null;
    this.availableLeafSitemaps = [];
    this.selectedSitemaps.clear();
    this.availableLocaleGroups = [];
    this.selectedLocales.clear();
    this.perSitemapLimits = {};
  }

  private estimateFromUploadedSitemaps(preserveSelection = false): void {
    const domain = this.normalizeDomain(this.newDomainInput);
    if (!domain || this.uploadedSitemapFiles.length === 0) return;
    this.estimateLoading = true;
    this.estimateError = null;
    if (!preserveSelection) {
      this.perSitemapLimits = {};
    }
    this.crawl
      .estimateCrawlFromUpload(domain, this.uploadedSitemapFiles, this.buildEstimateOptions(preserveSelection))
      .subscribe({
        next: (res) => this.applyEstimateResponse(res.data, preserveSelection),
        error: (err) => this.handleEstimateError(err)
      });
  }

  private buildEstimateOptions(preserveSelection: boolean) {
    return {
      limit: this.getEffectiveCrawlLimit(),
      selectedSitemaps: preserveSelection ? Array.from(this.selectedSitemaps) : [],
      selectedLocales: preserveSelection ? Array.from(this.selectedLocales) : [],
      minLastmodFrom: this.minLastmodFrom || null,
      perSitemapLimits: this.perSitemapLimits,
      perSitemapLimitMode: this.perSitemapLimitMode
    };
  }

  isDevView(): boolean {
    return !this.featureFlags.isProduction() && !this.featureFlags.isUserView();
  }

  /** Candidate domains for scoped topics:map: the focal domain + its competitors. */
  get topicsMapCandidates(): string[] {
    const candidates = [this.domain, ...this.competitors.map((c) => c.domain)]
      .map((d) => (d || '').trim().toLowerCase())
      .filter(Boolean);
    return [...new Set(candidates)];
  }

  onTopicsMapScopeChange(scope: 'all' | 'selected'): void {
    this.topicsMapScope = scope;
    // Prefill with all candidates when switching to "selected" so the common
    // case (focal + competitors) is one click.
    if (scope === 'selected') {
      this.topicsMapDomains = new Set(this.topicsMapCandidates);
    }
  }

  isTopicsMapDomainSelected(domain: string): boolean {
    return this.topicsMapDomains.has(domain);
  }

  toggleTopicsMapDomain(domain: string): void {
    if (this.topicsMapDomains.has(domain)) {
      this.topicsMapDomains.delete(domain);
    } else {
      this.topicsMapDomains.add(domain);
    }
  }

  private getEffectiveCrawlLimit(): number {
    return this.isDevView() && this.bypassCrawlCap ? this.devCrawlLimit : this.defaultCrawlLimit;
  }

  onBypassCapChange(): void {
    if (this.estimate) {
      this.refreshEstimateForFilters();
    }
  }

  private applyEstimateResponse(data: CrawlEstimate, preserveSelection: boolean): void {
    this.estimate = data;
    this.localePartitionedSitemaps = !!data.localePartitionedSitemaps;
    this.availableLeafSitemaps = this.collectLeafSitemaps(data?.sitemapTree || []);
    const validLeafSet = new Set(this.availableLeafSitemaps.map((e) => e.sitemapUrl));
    this.perSitemapLimits = Object.fromEntries(
      Object.entries(this.perSitemapLimits).filter(([url, v]) => validLeafSet.has(url) && v > 0)
    );

    this.availableLocaleGroups = data?.localeGroups || [];
    const validLocaleSet = new Set(this.availableLocaleGroups.map((g) => g.key));
    if (preserveSelection) {
      this.selectedLocales = new Set(
        Array.from(this.selectedLocales).filter((key) => validLocaleSet.has(key))
      );
    } else if (this.availableLocaleGroups.length > 0) {
      this.selectedLocales = new Set(
        validLocaleSet.has('__default__') ? ['__default__'] : []
      );
    } else {
      this.selectedLocales.clear();
    }

    if (preserveSelection) {
      this.selectedSitemaps = new Set(
        Array.from(this.selectedSitemaps).filter((url) => validLeafSet.has(url))
      );
    } else if (this.localePartitionedSitemaps) {
      this.syncSitemapSelectionToLocales();
    } else {
      this.selectedSitemaps = new Set(this.availableLeafSitemaps.map((e) => e.sitemapUrl));
    }

    if (!preserveSelection && this.availableLocaleGroups.length > 0 && this.selectedLocales.size > 0) {
      if (this.usingUploadedSitemaps) {
        this.estimateFromUploadedSitemaps(true);
      } else {
        this.estimateNewDomain(true);
      }
      return;
    }
    this.estimateLoading = false;
  }

  private syncSitemapSelectionToLocales(): void {
    if (!this.localePartitionedSitemaps) return;
    const leaves = this.getLeafNodes(this.estimate?.sitemapTree);
    this.selectedSitemaps = new Set(
      leaves
        .filter((leaf) => {
          const key = leaf.dominantLocaleKey || '__default__';
          return this.selectedLocales.has(key);
        })
        .map((leaf) => leaf.sitemapUrl)
    );
  }

  private handleEstimateError(err: unknown): void {
    this.estimate = null;
    const payload = (err as { error?: { error?: string; message?: string; code?: string } })?.error;
    const code = payload?.code || null;
    const message = payload?.error || payload?.message || (err as { message?: string })?.message || 'Failed to read sitemap for that domain.';
    if (code === 'BOT_PROTECTED') {
      this.botProtected = true;
      this.estimateError = null;
    } else {
      this.botProtected = false;
      this.estimateError = message;
    }
    this.estimateLoading = false;
  }

  get uploadedSitemapFileNames(): string {
    return this.uploadedSitemapFiles.map((f) => f.name).join(', ');
  }

  displaySitemapLabel(sitemapUrl: string): string {
    return sitemapUrl.startsWith('upload://') ? sitemapUrl.slice('upload://'.length) : sitemapUrl;
  }

  private async readUploadedSitemapPayload(): Promise<Array<{ filename: string; content: string }>> {
    const files = await Promise.all(
      this.uploadedSitemapFiles.map(async (file) => ({
        filename: file.name,
        content: await file.text()
      }))
    );
    return files;
  }

  trackSitemap(_index: number, node: SitemapTreeNode): string {
    return node.sitemapUrl;
  }

  startNewDomainCrawl(): void {
    const domain = this.normalizeDomain(this.newDomainInput);
    if (!domain || this.startingCrawl) return;
    if (this.estimate && !this.hasSitemapSelection()) {
      this.notifications.error('Select at least one sitemap to crawl.');
      return;
    }
    if (this.availableLocaleGroups.length > 0 && this.selectedLocales.size === 0) {
      this.notifications.error('Select at least one locale to crawl.');
      return;
    }
    const asCompetitor = this.addMode === 'competitor';
    const focalDomain = this.domain;
    this.startingCrawl = true;

    const start = (uploadedSitemaps: Array<{ filename: string; content: string }> = []) => {
      this.crawl
        .startCrawl(domain, {
          limit: this.getEffectiveCrawlLimit(),
          selectedSitemaps: Array.from(this.selectedSitemaps),
          selectedLocales: Array.from(this.selectedLocales),
          minLastmodFrom: this.minLastmodFrom || null,
          perSitemapLimits: this.perSitemapLimits,
          perSitemapLimitMode: this.perSitemapLimitMode,
          uploadedSitemaps
        })
        .subscribe({
          next: (res) => {
            this.startingCrawl = false;
            this.showAddDomain = false;
            this.newDomainInput = '';
            this.estimate = null;
            this.availableLeafSitemaps = [];
            this.selectedSitemaps.clear();
            this.availableLocaleGroups = [];
            this.selectedLocales.clear();
            this.botProtected = false;
            this.uploadedSitemapFiles = [];
            this.usingUploadedSitemaps = false;
            const canonical = (res.canonicalDomain || domain).toLowerCase().replace(/^www\./, '');

            if (asCompetitor && focalDomain) {
              this.notifications.success(
                res?.message || `Crawl started for ${canonical}. It will be added as a competitor of ${focalDomain}.`
              );
              this.compareContext.addMarkedCompetitor(canonical, focalDomain).subscribe({
                next: () => {
                  this.domainContext.refresh(focalDomain);
                  this.loadAll(false);
                },
                error: (err) =>
                  this.notifications.error(
                    err?.error?.error || err?.error?.message || err?.message || 'Crawl started but failed to mark as competitor.'
                  )
              });
            } else {
              this.notifications.success(res?.message || `Crawl started for ${canonical}. Finish setup once it completes.`);
              this.domainContext.refresh(canonical);
            }
          },
          error: (err) => {
            this.startingCrawl = false;
            this.notifications.error(err?.error?.error || err?.error?.message || err?.message || 'Failed to start crawl.');
          }
        });
    };

    if (this.usingUploadedSitemaps && this.uploadedSitemapFiles.length > 0) {
      this.readUploadedSitemapPayload()
        .then((uploadedSitemaps) => start(uploadedSitemaps))
        .catch(() => {
          this.startingCrawl = false;
          this.notifications.error('Could not read uploaded sitemap files.');
        });
      return;
    }
    start();
  }

  // --- Sitemap scoping helpers (mirror Crawl Management) ------------------

  private collectLeafSitemaps(nodes: SitemapTreeNode[]): Array<{ sitemapUrl: string; urlCount: number }> {
    const leaves: Array<{ sitemapUrl: string; urlCount: number }> = [];
    const walk = (node: SitemapTreeNode) => {
      if (node.type === 'urlset') leaves.push({ sitemapUrl: node.sitemapUrl, urlCount: node.urlCount || 0 });
      (node.children || []).forEach(walk);
    };
    nodes.forEach(walk);
    return leaves;
  }

  getLeafNodes(nodes: SitemapTreeNode[] | undefined): SitemapTreeNode[] {
    if (!nodes || nodes.length === 0) return [];
    const leaves: SitemapTreeNode[] = [];
    const walk = (node: SitemapTreeNode) => {
      if (node.type === 'urlset') leaves.push(node);
      (node.children || []).forEach(walk);
    };
    nodes.forEach(walk);
    return leaves;
  }

  getLeafDisplayUrlCount(node: SitemapTreeNode): number {
    const base = this.minLastmodFrom ? (node.filteredUrlCount ?? 0) : (node.urlCount || 0);
    const cap = this.getPerSitemapLimit(node.sitemapUrl);
    return cap ? Math.min(base, cap) : base;
  }

  getPerSitemapLimit(sitemapUrl: string): number | null {
    const value = this.perSitemapLimits[sitemapUrl];
    return Number.isFinite(value) && value > 0 ? value : null;
  }

  onPerSitemapLimitChange(sitemapUrl: string, value: string | number | null): void {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) {
      this.perSitemapLimits[sitemapUrl] = Math.floor(parsed);
    } else {
      delete this.perSitemapLimits[sitemapUrl];
    }
    this.refreshEstimateForFilters();
  }

  onMinLastmodFromChange(value: string): void {
    this.minLastmodFrom = value;
    this.refreshEstimateForFilters();
  }

  onPerSitemapLimitModeChange(value: 'recent' | 'oldest' | 'balanced'): void {
    this.perSitemapLimitMode = value;
    this.refreshEstimateForFilters();
  }

  toggleSitemapSelection(sitemapUrl: string, checked: boolean): void {
    if (checked) {
      this.selectedSitemaps.add(sitemapUrl);
    } else {
      this.selectedSitemaps.delete(sitemapUrl);
    }
    this.refreshEstimateForFilters();
  }

  setAllSitemapsSelected(checked: boolean): void {
    if (checked) {
      this.selectedSitemaps = new Set(this.availableLeafSitemaps.map((e) => e.sitemapUrl));
    } else {
      this.selectedSitemaps.clear();
    }
    this.refreshEstimateForFilters();
  }

  areAllSitemapsSelected(): boolean {
    return this.availableLeafSitemaps.length > 0 && this.selectedSitemaps.size === this.availableLeafSitemaps.length;
  }

  hasSitemapSelection(): boolean {
    return this.selectedSitemaps.size > 0;
  }

  // --- Locale scoping helpers --------------------------------------------

  toggleLocaleSelection(key: string, checked: boolean): void {
    if (checked) {
      this.selectedLocales.add(key);
    } else {
      this.selectedLocales.delete(key);
    }
    if (this.localePartitionedSitemaps) {
      this.syncSitemapSelectionToLocales();
    }
    this.refreshEstimateForFilters();
  }

  setAllLocalesSelected(checked: boolean): void {
    if (checked) {
      this.selectedLocales = new Set(this.availableLocaleGroups.map((g) => g.key));
    } else {
      this.selectedLocales.clear();
    }
    if (this.localePartitionedSitemaps) {
      this.syncSitemapSelectionToLocales();
    }
    this.refreshEstimateForFilters();
  }

  areAllLocalesSelected(): boolean {
    return this.availableLocaleGroups.length > 0 && this.selectedLocales.size === this.availableLocaleGroups.length;
  }

  trackLocaleGroup(_index: number, group: LocaleGroup): string {
    return group.key;
  }

  getSelectedDiscoveredUrlCount(): number {
    return this.estimate?.selectedPagesInSitemap ?? 0;
  }

  getSelectedIncludedAtCap(): number {
    return this.estimate?.selectedFilteredPages ?? 0;
  }

  getSelectedEstimatedCost(): number {
    if (!this.estimate) return 0;
    return this.estimate.selectedEstimatedTotalCost ?? (this.getSelectedIncludedAtCap() * this.estimate.estimatedCostPerPage);
  }

  private refreshEstimateForFilters(): void {
    if (!this.estimate) return;
    if (this.estimateDebounce) clearTimeout(this.estimateDebounce);
    this.estimateDebounce = setTimeout(() => {
      this.estimateDebounce = null;
      if (this.usingUploadedSitemaps) {
        this.estimateFromUploadedSitemaps(true);
      } else {
        this.estimateNewDomain(true);
      }
    }, 600);
  }

  // --- Display helpers ----------------------------------------------------

  stateLabel(state: SetupStepState): string {
    switch (state) {
      case 'no_data':
        return 'No data';
      case 'not_started':
        return 'Not started';
      case 'waiting':
        return 'Waiting for crawl';
      case 'queued':
        return 'Queued';
      case 'running':
        return 'Running';
      case 'partial':
        return 'Partial';
      case 'complete':
        return 'Complete';
      case 'blocked':
        return 'Blocked';
      case 'failed':
        return 'Failed';
      default:
        return state;
    }
  }

  stateClass(state: SetupStepState): string {
    return `state-${state}`;
  }

  /** Sidecar-down hint for the opportunities step. */
  isSidecarBlocked(step: SetupStep): boolean {
    if (step.key !== 'supporting-opportunities') return false;
    const reason = (step.blockedReason || '').toLowerCase();
    return step.state === 'blocked' && (reason.includes('sidecar') || reason.includes('content-gap') || reason.includes('content gap'));
  }
}
